import { z } from 'zod'
import { DEVISE_PAR_DEFAUT } from './domaine'
import { normaliserLibelle } from './libelle'
import { analyserMontant } from './montant'
import type { NatureOperation, OperationBancaire } from './operations-bancaires'
import { pieceAttendue } from './recherche-pieces'

// Dougs, dossier comptable de SC PMVB et de la LMNP (CLAUDE.md, « Dépôt et affectation des
// justificatifs chez le cabinet : Tiime et Dougs ») : lecture des réponses de son API pour
// l'adaptateur de l'agent du Mac (onglet prêté du Chrome de tous les jours, session du titulaire)
// et pour le moteur des justificatifs. Fonctions pures : ni réseau, ni écriture, et aucune
// écriture déclarée ici.
//
// Ce que l'API rend est une donnée non fiable. Les formes sont revalidées : une clé inconnue est
// tolérée, un type faux sur un champ utile refusé. Les textes sont bornés et sans signe de
// contrôle ; le texte d'une page n'est jamais une consigne. Rien n'est rendu de ce qui ne sert
// pas : ni adresse signée d'un fichier, ni mémo, ni courriel du compte.
//
// Écritures connues, à déclarer plus tard par l'adaptateur (méthode, domaine, chemin), jamais
// ici (le dépôt d'une pièce l'est, `ECRITURES_METIER`) :
// - poser la catégorie, et valider (`validated: true`) : POST /companies/{c}/operations/{op}, le
//   corps étant l'opération entière relue juste avant ; seule la catégorie de la ventilation hors
//   contrepartie doit y changer (à vérifier avant l'envoi), et jamais sur une opération ventilée ;
//   une assurance exige d'abord, dans un envoi à part, `associationData` { insuranceType,
//   insurancesPeriod « AAAA-MM » } (relevé le 12/10/2026 : les deux ensemble sont refusés).

// Domaines

/** L'appli et son API, sur le même hôte ; session par cookie, celle du titulaire. */
export const DOUGS_DOMAINES: readonly string[] = ['app.dougs.fr']

/**
 * Fichiers des pièces : adresse signée sur le compartiment de Dougs, lue en GET seulement et
 * jamais gardée. À part des domaines : déclarer l'hôte entier ouvrirait tous les compartiments
 * de la région.
 */
export const DOUGS_FICHIERS = {
  hote: 's3.eu-west-3.amazonaws.com',
  prefixe: '/files.dougs.fr/',
} as const

/** Une adresse de fichier de Dougs, lue en GET, en https, dans son seul compartiment. */
export function fichierDougsPermis(adresse: string, methode: string): boolean {
  if (methode.toUpperCase() !== 'GET') return false
  let u: URL
  try {
    u = new URL(adresse)
  } catch {
    return false
  }
  if (u.protocol !== 'https:' || u.hostname !== DOUGS_FICHIERS.hote || u.port !== '') return false
  if (u.username !== '' || u.password !== '' || /%2e|%2f|%5c/i.test(u.pathname)) return false
  return (
    u.pathname.startsWith(DOUGS_FICHIERS.prefixe) &&
    u.pathname.length > DOUGS_FICHIERS.prefixe.length
  )
}

// Chemins : lecture seule

/**
 * Les seules lectures permises, en GET, chemin exact (sans paramètres) ; les identifiants de
 * dossier sont numériques. La page des opérations de l'appli s'appelle « …/operations/payments » :
 * c'est une liste à lire, pas un paiement.
 */
export const LECTURES_DOUGS = [
  { lecture: 'utilisateur', chemin: /^\/users\/me\/?$/ },
  { lecture: 'operations', chemin: /^\/companies\/[1-9]\d{0,15}\/operations\/?$/ },
  { lecture: 'categories', chemin: /^\/companies\/[1-9]\d{0,15}\/categories\/?$/ },
  { lecture: 'canaux_de_vente', chemin: /^\/companies\/[1-9]\d{0,15}\/salesChannels\/?$/ },
  // Une seule opération (sonde de relecture du passage), relevée le 12/10/2026 : l'objet seul.
  {
    lecture: 'operation',
    chemin: /^\/companies\/[1-9]\d{0,15}\/operations\/[1-9]\d{0,15}\/?$/,
  },
  {
    lecture: 'page_operations',
    chemin: /^\/app\/c\/[1-9]\d{0,15}\/accounting\/operations\/payments\/?$/,
  },
] as const satisfies readonly { lecture: string; chemin: RegExp }[]
export type LectureDougs = (typeof LECTURES_DOUGS)[number]['lecture']

/**
 * Ce que CLAUDE.md interdit chez Dougs, sur les chemins de l'appli (/app/c/{c}/…) comme de l'API,
 * jugé sur le chemin en minuscules. Hors de la liste blanche, tout est déjà refusé : les zones
 * nomment le refus et resservent à l'interception de l'adaptateur. Les premières l'emportent.
 * Le bilan, la liasse, le questionnaire de bilan, les avis d'acompte et les déclarations
 * déclenchent une télétransmission à leur validation : geste N2.
 */
export const ZONES_INTERDITES_DOUGS = [
  { zone: 'questionnaire_bilan', motifs: [/questionnaire/, /surveys?/] },
  { zone: 'liasse', motifs: [/liasse/, /tax-?returns?/, /fiscal-?(package|return)s?/] },
  {
    zone: 'avis_acompte',
    motifs: [/acomptes?/, /installments?/, /prepayments?/, /down-?payments?/, /advance-?payments?/],
  },
  {
    zone: 'bilan',
    motifs: [/bilan/, /balance-?sheets?/, /closing/, /cloture/, /annual-?accounts?/],
  },
  {
    zone: 'declarations',
    motifs: [/declarations?/, /\bvat\b/, /\btva\b/, /teletransm/, /filings?/, /\btax(es)?\b/],
  },
  {
    // Bénéficiaire en anglais comme en français (beneficiaries, beneficiaires).
    zone: 'virements',
    motifs: [
      /transfers?/,
      /virements?/,
      /sepa/,
      /benefici/,
      /mandat/,
      /direct-?debits?/,
      /prelevements?/,
    ],
  },
  {
    zone: 'paiements',
    motifs: [
      // Tout « payment » (moyens, demandes, échéancier…), sauf la seule page des opérations de
      // l'appli, chemin exact : « /app/c/{c}/accounting/operations/payments », liste à lire.
      /^(?!\/app\/c\/\d+\/accounting\/operations\/payments\/?$).*payment/,
      /payouts?/,
      /paiements?/,
      /\/pay(\/|$)/,
      /checkout/,
      /subscriptions?/,
      /billing/,
      /cards?/,
      /cartes?/,
    ],
  },
  {
    // Coordonnées bancaires et de l'entreprise, profil et identifiants du compte.
    zone: 'coordonnees',
    motifs: [
      /iban/,
      /\bbic\b/,
      /\brib\b/,
      /bank-?details/,
      /bank-?accounts?/,
      /coordonnees/,
      /settings/,
      /profile?/,
      /password/,
      /security/,
      /\b(2fa|mfa|two-?factor)\b/,
    ],
  },
  {
    // Suppression ou modification d'une opération autre que sa catégorie (écriture à venir,
    // sur l'opération elle-même) : suppression, exclusion, découpage, opérations groupées.
    zone: 'modification_operation',
    motifs: [
      /\/operations\/(bulk|batch)(\/|$)/,
      /\/(delete|remove|destroy|exclude|split|merge|duplicate|unvalidate|invalidate)(\/|$)/,
    ],
  },
] as const satisfies readonly { zone: string; motifs: readonly RegExp[] }[]
export type ZoneInterditeDougs = (typeof ZONES_INTERDITES_DOUGS)[number]['zone']

const CHEMIN_MAX = 2000

const cheminSansSuite = (chemin: string) => chemin.split(/[?#]/)[0] ?? ''

/** La zone interdite où tombe ce chemin (paramètres ignorés), ou null. */
export function zoneInterditeDougs(chemin: string): ZoneInterditeDougs | null {
  const c = cheminSansSuite(chemin).toLowerCase()
  return ZONES_INTERDITES_DOUGS.find((zone) => zone.motifs.some((m) => m.test(c)))?.zone ?? null
}

/**
 * Chemin absolu en ASCII visible, borné ; ni « // », ni « . » ou « .. », ni « % » ni « \ » dans le
 * chemin (un encodage ferait sortir d'une zone ou entrer dans la liste) ; les paramètres peuvent
 * être encodés.
 */
function cheminLisible(chemin: string): boolean {
  if (chemin.length === 0 || chemin.length > CHEMIN_MAX) return false
  if (!/^[\x21-\x7e]+$/.test(chemin)) return false
  const c = cheminSansSuite(chemin)
  if (!c.startsWith('/') || c.includes('//') || /[%\\]/.test(c)) return false
  return !c.split('/').some((s) => s === '.' || s === '..')
}

export type DecisionCheminDougs =
  | { permis: true; lecture: LectureDougs }
  | { permis: false; motif: 'methode' | 'illisible' | 'hors_liste' }
  | { permis: false; motif: 'zone'; zone: ZoneInterditeDougs }

/**
 * Une requête vers app.dougs.fr : GET seulement, sur un chemin de la liste blanche, hors des
 * zones interdites ; tout le reste est refusé. Le chemin peut porter ses paramètres.
 */
export function cheminDougsPermis(chemin: string, methode: string): DecisionCheminDougs {
  if (methode.toUpperCase() !== 'GET') return { permis: false, motif: 'methode' }
  if (!cheminLisible(chemin)) return { permis: false, motif: 'illisible' }
  const zone = zoneInterditeDougs(chemin)
  if (zone) return { permis: false, motif: 'zone', zone }
  const c = cheminSansSuite(chemin)
  const lecture = LECTURES_DOUGS.find((l) => l.chemin.test(c))
  return lecture
    ? { permis: true, lecture: lecture.lecture }
    : { permis: false, motif: 'hors_liste' }
}

// Réponses de l'API (schémas tolérants)

/** La réponse n'a pas la forme relevée : rien n'en est lu. Le message ne reprend rien d'elle. */
export class ReponseDougsIllisible extends Error {
  constructor(readonly lecture: 'utilisateur' | 'operations' | 'categories') {
    super(`Réponse de Dougs illisible (${lecture}) : forme inattendue, rien n’est lu.`)
    this.name = 'ReponseDougsIllisible'
  }
}

export const DOSSIERS_DOUGS_MAX = 100
/** Opérations lues d'une réponse, au plus ; le reste est compté (« au_dela »). */
export const OPERATIONS_DOUGS_MAX = 1000
const VENTILATIONS_MAX = 200
const PIECES_JOINTES_MAX = 200
const CATEGORIES_MAX = 5000
const NOM_MAX = 120
const LIBELLE_MAX = 300
const MARCHAND_MAX = 200
const CATEGORIE_MAX = 120
/** Comme operationBancaireEntree (recherche-pieces.ts) : au-delà, le montant est illisible. */
const MONTANT_MAX_CTS = 10_000_000_000

const identifiant = z.number().int().positive()
const texte = z.string().nullish()

/** Un dossier (société) de GET /users/me. */
export const societeDougsLue = z.looseObject({
  id: identifiant,
  siren: texte,
  legalName: texte,
  brandName: texte,
  fullName: texte,
})

/** GET /users/me : seuls les dossiers comptent. */
export const utilisateurDougsLu = z.looseObject({
  companies: z.array(societeDougsLue).max(DOSSIERS_DOUGS_MAX),
})

const enteteOperationDougs = z.looseObject({ id: identifiant, type: z.string() })

const transactionDougsLue = z.looseObject({
  currency: texte,
  wording: texte,
  counterpartyName: texte,
})

const ventilationDougsLue = z.looseObject({
  categoryId: identifiant.nullish(),
  categoryWording: texte,
  autocategorizationMethod: texte,
  isCounterpart: z.boolean(),
  isManuallyCategorized: z.boolean().nullish(),
  amount: z.number(),
})

const pieceJointeDougsLue = z.looseObject({ sourceDocument: z.looseObject({}).nullish() })

/** Une opération bancaire de GET /companies/{c}/operations. */
export const operationDougsLue = z.looseObject({
  id: identifiant,
  companyId: identifiant.nullish(),
  type: z.string(),
  wording: texte,
  amount: z.number(),
  isInbound: z.boolean(),
  date: z.iso.date(),
  validated: z.boolean(),
  deleted: z.boolean(),
  isDraft: z.boolean(),
  excluded: z.boolean(),
  transaction: transactionDougsLue.nullish(),
  breakdowns: z.array(ventilationDougsLue).max(VENTILATIONS_MAX),
  sourceDocumentAttachments: z.array(pieceJointeDougsLue).max(PIECES_JOINTES_MAX),
  sourceDocumentCandidate: z.looseObject({}).nullish(),
})

const documentsOperationLus = z.looseObject({
  sourceDocumentAttachments: z
    .array(
      z.looseObject({
        sourceDocument: z.looseObject({ id: identifiant }).nullish(),
      }),
    )
    .max(PIECES_JOINTES_MAX),
})

/**
 * Les documents rattachés à une opération, en texte (`sourceDocument.id`, celui du registre), une
 * pièce sans document ignorée ; null si l'opération ne se lit pas : jamais « rattachée à rien ».
 */
export function documentsOperationDougs(operation: unknown): string[] | null {
  const lu = documentsOperationLus.safeParse(operation)
  if (!lu.success) return null
  return lu.data.sourceDocumentAttachments.flatMap((p) =>
    p.sourceDocument ? [String(p.sourceDocument.id)] : [],
  )
}

/** GET /companies/{c}/categories : des identifiants, sans libellé. */
export const categoriesDougsLues = z.array(identifiant).max(CATEGORIES_MAX)

// Textes et nombres lus

// Signes de contrôle (C0, C1) : une espace. Retirés : la mise en forme invisible (Cf : largeur
// nulle, sens d'écriture, trait d'union conditionnel, balises U+E0000 à U+E007F qui portent un
// texte caché lisible par un modèle), la zone privée (Co), les points non attribués (Cn), une
// demi-paire de substitution seule (Cs) et les sélecteurs de variante, qui peuvent aussi cacher
// un texte.
const CONTROLES = /\p{Cc}/gu
const INVISIBLES = /[\p{Cf}\p{Co}\p{Cn}\p{Cs}\p{Variation_Selector}]/gu

/** Un texte lu : sans signe de contrôle ni mise en forme invisible, espaces réduites, borné. */
export function texteLu(v: string | null | undefined, max: number): string | null {
  if (typeof v !== 'string') return null
  const propre = v.replace(CONTROLES, ' ').replace(INVISIBLES, '').replace(/\s+/g, ' ').trim()
  return [...propre].slice(0, max).join('').trim() || null
}

/**
 * Centimes exacts d'un montant en euros lu en nombre décimal : sa forme la plus courte en texte
 * (celle du JSON reçu), lue par analyserMontant ; plus de deux décimales ou notation exponentielle :
 * null. Valeur absolue, le signe se lit ailleurs.
 */
export function centimesLus(montant: number): number | null {
  const c = analyserMontant(String(Math.abs(montant)))
  return c === null || c > MONTANT_MAX_CTS ? null : c
}

function sirenLu(s: string | null | undefined): string | null {
  if (typeof s !== 'string') return null
  const c = s.replace(/\s/g, '')
  return /^\d{9}$/.test(c) ? c : null
}

function deviseLue(d: string | null | undefined): string | null {
  const t = d?.trim().toUpperCase() ?? ''
  if (t === '') return DEVISE_PAR_DEFAUT
  return /^[A-Z]{3}$/.test(t) ? t : null
}

// Dossiers

/** Un dossier Dougs : sa référence (identifiant en texte), son SIREN s'il est lisible, son nom. */
export type DossierDougs = { ref: string; siren: string | null; nom: string }

/**
 * Les dossiers de GET /users/me, une fois chacun. SIREN : neuf chiffres (espaces retirées), sinon
 * null. Nom : raison sociale, sinon nom commercial, sinon nom complet. Une réponse d'une autre
 * forme est refusée en entier : un dossier oublié fausserait le choix par SIREN.
 */
export function dossiersDougs(me: unknown): DossierDougs[] {
  const lu = utilisateurDougsLu.safeParse(me)
  if (!lu.success) throw new ReponseDougsIllisible('utilisateur')
  const vus = new Set<number>()
  const dossiers: DossierDougs[] = []
  for (const c of lu.data.companies) {
    if (vus.has(c.id)) continue
    vus.add(c.id)
    const ref = String(c.id)
    dossiers.push({
      ref,
      siren: sirenLu(c.siren),
      nom:
        texteLu(c.legalName, NOM_MAX) ??
        texteLu(c.brandName, NOM_MAX) ??
        texteLu(c.fullName, NOM_MAX) ??
        `Dossier ${ref}`,
    })
  }
  return dossiers
}

export type ChoixDossierDougs = { dossier: DossierDougs } | { refus: 'aucun' | 'plusieurs' }

/**
 * Le dossier de l'entité : le seul dont le SIREN est le sien. Aucun, ou une entité sans SIREN
 * lisible : « aucun » ; plusieurs : « plusieurs ». Jamais par le nom, jamais au hasard.
 */
export function choisirDossierDougs(
  dossiers: readonly DossierDougs[],
  siren: string | null,
): ChoixDossierDougs {
  const cherche = sirenLu(siren)
  if (cherche === null) return { refus: 'aucun' }
  const trouves = dossiers.filter((d) => sirenLu(d.siren) === cherche)
  if (trouves.length > 1) return { refus: 'plusieurs' }
  const [dossier] = trouves
  return dossier ? { dossier } : { refus: 'aucun' }
}

// Catégories

/** Les catégories d'un dossier, une fois chacune ; une autre forme est refusée. */
export function categoriesDougs(lues: unknown): number[] {
  const r = categoriesDougsLues.safeParse(lues)
  if (!r.success) throw new ReponseDougsIllisible('categories')
  return [...new Set(r.data)]
}

// Opérations

export type CategorieDougs = { id: number; libelle: string }

/**
 * D'où vient la catégorie : apprise d'une opération validée (« wording:operationId-n »), d'un
 * début de libellé (« prefixWording:… »), d'un mot-clé (« keyword:… »), ou posée à la main.
 */
export const ORIGINES_CATEGORIE_DOUGS = ['appris', 'prefixe', 'mot_cle', 'manuelle'] as const
export type OrigineCategorieDougs = (typeof ORIGINES_CATEGORIE_DOUGS)[number]

/** Une opération bancaire de Dougs, vue comme toute opération bancaire de l'appli. */
export type OperationDougs = OperationBancaire & {
  /** Identifiant de l'opération chez Dougs, en texte. */
  ref: string
  validee: boolean
  /** Pièces jointes à l'opération. */
  justificatifs: number
  /** Dougs propose lui-même une pièce (à rapprocher, jamais acceptée d'office). */
  pieceProposee: boolean
  /**
   * Catégorie de la ventilation hors contrepartie ; plusieurs : la plus forte en montant. Null
   * quand aucune ligne n'en a, ou qu'une ligne reste « Non catégorisé ».
   */
  categorie: CategorieDougs | null
  /** Lignes de catégories différentes : jamais recatégorisée d'office. */
  ventilee: boolean
  /**
   * Origine commune à toutes les lignes hors contrepartie ; null si elles diffèrent, si l'une
   * n'en dit rien ou s'il n'y a pas de catégorie. « appris » vaut donc pour toute l'opération.
   */
  categorieApprise: OrigineCategorieDougs | null
}

/** Pourquoi une opération lue n'est pas rendue ; dans cet ordre dans le compte. */
export const RAISONS_ECART_DOUGS = [
  'illisible',
  'hors_banque',
  'supprimee',
  'brouillon',
  'exclue',
  'autre_dossier',
  'doublon',
  'au_dela',
] as const
export type RaisonEcartDougs = (typeof RAISONS_ECART_DOUGS)[number]

export type LectureOperationsDougs = {
  operations: OperationDougs[]
  ecartees: { raison: RaisonEcartDougs; nombre: number }[]
}

// Le moyen de paiement, que la banque écrit en tête du libellé (normalisé). Plus loin, le mot ne
// dit pas comment l'opération a été payée : « COTISATION CARTE », « FRAIS CB », « COMMISSION
// PAIEMENT PAR CARTE » sont des frais bancaires, sans pièce (LIBELLES_SANS_PIECE), et non des
// paiements par carte, qui attendent toujours la leur.
const MOTS_NATURE: readonly { nature: NatureOperation; motif: RegExp }[] = [
  { nature: 'prelevement', motif: /^(PRELEVEMENTS?|PRLV)\b/ },
  { nature: 'virement', motif: /^(VIREMENTS?|VIR)\b/ },
  { nature: 'carte', motif: /^((ACHAT|FACTURE|PAIEMENT)( PAR)? )?(CARTE|CB)\b/ },
]

/** Nature d'après le moyen de paiement en tête du libellé, sinon « autre ». */
export function natureOperationDougs(libelle: string): NatureOperation {
  const l = normaliserLibelle(libelle)
  return MOTS_NATURE.find(({ motif }) => motif.test(l))?.nature ?? 'autre'
}

const NON_CATEGORISE = /^NON CATEGORISEE?S?$/

function origineLue(v: z.output<typeof ventilationDougsLue>): OrigineCategorieDougs | null {
  if (v.isManuallyCategorized === true) return 'manuelle'
  const m = v.autocategorizationMethod ?? ''
  if (/^wording:operationId-\d+(:|$)/.test(m)) return 'appris'
  if (/^prefixWording:operationId-\d+:/.test(m)) return 'prefixe'
  if (/^keyword:categoryId-\d+:/.test(m)) return 'mot_cle'
  return null
}

type LigneVentilation = {
  categorie: CategorieDougs | null
  montantCts: number
  origine: OrigineCategorieDougs | null
}

/**
 * Catégorie d'une opération d'après ses ventilations hors contrepartie ; null si l'une est
 * illisible (catégorie sans libellé, montant à plus de deux décimales).
 */
function categorieDe(ventilations: readonly z.output<typeof ventilationDougsLue>[]): {
  categorie: CategorieDougs | null
  ventilee: boolean
  origine: OrigineCategorieDougs | null
} | null {
  const lignes: LigneVentilation[] = []
  for (const v of ventilations) {
    if (v.isCounterpart) continue
    const montantCts = centimesLus(v.amount)
    if (montantCts === null) return null
    if (v.categoryId === null || v.categoryId === undefined) {
      lignes.push({ categorie: null, montantCts, origine: null })
      continue
    }
    const libelle = texteLu(v.categoryWording, CATEGORIE_MAX)
    if (libelle === null) return null
    lignes.push(
      NON_CATEGORISE.test(normaliserLibelle(libelle))
        ? { categorie: null, montantCts, origine: null }
        : { categorie: { id: v.categoryId, libelle }, montantCts, origine: origineLue(v) },
    )
  }
  const ventilee = new Set(lignes.map((l) => l.categorie?.id ?? null)).size > 1
  const categorisees = lignes.filter(
    (l): l is LigneVentilation & { categorie: CategorieDougs } => l.categorie !== null,
  )
  const [premiere] = categorisees
  if (!premiere || categorisees.length < lignes.length)
    return { categorie: null, ventilee, origine: null }
  // La plus forte en montant, lignes d'une même catégorie additionnées ; à égalité, la première.
  const sommes = new Map<number, number>()
  for (const l of categorisees)
    sommes.set(l.categorie.id, (sommes.get(l.categorie.id) ?? 0) + l.montantCts)
  let retenue = premiere.categorie
  for (const l of categorisees)
    if ((sommes.get(l.categorie.id) ?? 0) > (sommes.get(retenue.id) ?? 0)) retenue = l.categorie
  // L'origine vaut pour toute l'opération : celle de chaque ligne, sinon null. Une part classée
  // par mot-clé ou à la main ne passe jamais pour apprise d'une opération validée.
  const origine = categorisees.every((l) => l.origine === premiere.origine)
    ? premiere.origine
    : null
  return { categorie: retenue, ventilee, origine }
}

function lireOperation(
  brute: unknown,
  contexte: { entiteId: string; dossier?: string },
): OperationDougs | RaisonEcartDougs {
  const entete = enteteOperationDougs.safeParse(brute)
  if (!entete.success) return 'illisible'
  // Une dépense, une écriture manuelle… : ni banque ni forme d'opération bancaire à exiger.
  if (entete.data.type !== 'bank') return 'hors_banque'
  const lu = operationDougsLue.safeParse(brute)
  if (!lu.success) return 'illisible'
  const o = lu.data
  if (o.deleted) return 'supprimee'
  if (o.isDraft) return 'brouillon'
  if (o.excluded) return 'exclue'
  if (
    contexte.dossier !== undefined &&
    (o.companyId === null || o.companyId === undefined || String(o.companyId) !== contexte.dossier)
  )
    return 'autre_dossier'
  const montant = centimesLus(o.amount)
  const libelle = texteLu(o.transaction?.wording, LIBELLE_MAX) ?? texteLu(o.wording, LIBELLE_MAX)
  const devise = deviseLue(o.transaction?.currency)
  const categorie = categorieDe(o.breakdowns)
  if (!montant || libelle === null || devise === null || categorie === null) return 'illisible'
  return {
    entiteId: contexte.entiteId,
    ref: String(o.id),
    date: o.date,
    // Le signe lu n'est pas sûr : crédit positif, débit négatif, d'après isInbound.
    montantCts: o.isInbound ? montant : -montant,
    devise,
    libelle,
    marchand: texteLu(o.transaction?.counterpartyName, MARCHAND_MAX),
    nature: natureOperationDougs(libelle),
    validee: o.validated,
    justificatifs: o.sourceDocumentAttachments.filter((p) => (p.sourceDocument ?? null) !== null)
      .length,
    pieceProposee: (o.sourceDocumentCandidate ?? null) !== null,
    categorie: categorie.categorie,
    ventilee: categorie.ventilee,
    categorieApprise: categorie.origine,
  }
}

/**
 * Les opérations bancaires d'une réponse de GET /companies/{c}/operations : seulement celles de
 * type « bank », ni supprimées, ni brouillon, ni exclues ; une même opération une seule fois (la
 * première). Les autres sont comptées par raison. Dossier donné : une opération d'un autre
 * dossier, ou sans dossier, est écartée. Une réponse qui n'est pas une liste est refusée.
 */
export function operationsDougs(
  lues: unknown,
  contexte: { entiteId: string; dossier?: string },
): LectureOperationsDougs {
  if (!Array.isArray(lues)) throw new ReponseDougsIllisible('operations')
  const comptes = new Map<RaisonEcartDougs, number>()
  const ecarter = (raison: RaisonEcartDougs, nombre = 1) =>
    comptes.set(raison, (comptes.get(raison) ?? 0) + nombre)
  const operations: OperationDougs[] = []
  const vues = new Set<string>()
  for (const brute of lues.slice(0, OPERATIONS_DOUGS_MAX)) {
    const op = lireOperation(brute, contexte)
    if (typeof op === 'string') ecarter(op)
    else if (vues.has(op.ref)) ecarter('doublon')
    else {
      vues.add(op.ref)
      operations.push(op)
    }
  }
  if (lues.length > OPERATIONS_DOUGS_MAX) ecarter('au_dela', lues.length - OPERATIONS_DOUGS_MAX)
  return {
    operations,
    ecartees: RAISONS_ECART_DOUGS.filter((r) => comptes.has(r)).map((raison) => ({
      raison,
      nombre: comptes.get(raison)!,
    })),
  }
}

// Vers le socle des justificatifs

/**
 * Une opération dans la forme brute que lit le socle des justificatifs
 * (`normaliserOperationsPortail`) : montant signé en euros, en texte exact à deux décimales.
 * Dougs n'a pas d'opération provisoire ; « ventilée » y veut dire « lignes de catégories
 * différentes » : jamais sûre de même.
 */
export type LigneJustificatifsDougs = {
  ref: string
  date: string
  montant: string
  devise: string
  libelle: string
  marchand: string | null
  nature: NatureOperation
  justificatifs: number
  ventilee: boolean
  provisoire: false
  validee: boolean
}

const euros = (cts: number) =>
  `${cts < 0 ? '-' : ''}${Math.floor(Math.abs(cts) / 100)}.${String(Math.abs(cts) % 100).padStart(2, '0')}`

/**
 * Les opérations d'une réponse de GET /companies/{c}/operations (ou l'objet seul d'une opération,
 * dans une liste) pour le socle des justificatifs, dans l'ordre lu. Une opération illisible reste
 * une ligne, nulle : le socle la compte et la lecture est incomplète (rien de sûr) ; une opération
 * hors banque, supprimée, brouillon, exclue ou d'un autre dossier n'est pas une ligne, seulement
 * comptée. Les doublons sont laissés au socle. Une réponse qui n'est pas une liste est refusée.
 */
export function lignesJustificatifsDougs(
  lues: unknown,
  dossier: string,
): {
  lignes: (LigneJustificatifsDougs | null)[]
  ecartees: { raison: RaisonEcartDougs; nombre: number }[]
} {
  if (!Array.isArray(lues)) throw new ReponseDougsIllisible('operations')
  const comptes = new Map<RaisonEcartDougs, number>()
  const lignes: (LigneJustificatifsDougs | null)[] = []
  for (const brute of lues.slice(0, OPERATIONS_DOUGS_MAX)) {
    // L'entité n'est jamais connue de l'agent : la ligne n'en porte pas.
    const op = lireOperation(brute, { entiteId: '', dossier })
    if (typeof op === 'string') {
      comptes.set(op, (comptes.get(op) ?? 0) + 1)
      if (op === 'illisible') lignes.push(null)
      continue
    }
    lignes.push({
      ref: op.ref,
      date: op.date,
      montant: euros(op.montantCts),
      devise: op.devise,
      libelle: op.libelle,
      marchand: op.marchand,
      nature: op.nature,
      justificatifs: op.justificatifs,
      ventilee: op.ventilee,
      provisoire: false,
      validee: op.validee,
    })
  }
  if (lues.length > OPERATIONS_DOUGS_MAX) comptes.set('au_dela', lues.length - OPERATIONS_DOUGS_MAX)
  return {
    lignes,
    ecartees: RAISONS_ECART_DOUGS.filter((r) => comptes.has(r)).map((raison) => ({
      raison,
      nombre: comptes.get(raison)!,
    })),
  }
}

// État d'une opération

export const ETATS_OPERATION_DOUGS = ['validee', 'a_qualifier', 'a_justifier', 'prete'] as const
export type EtatOperationDougs = (typeof ETATS_OPERATION_DOUGS)[number]

/**
 * Où en est une opération : validée ; à qualifier (pas de catégorie) ; à justifier (catégorie
 * posée, pièce attendue, aucune pièce jointe) ; prête (catégorie et pièce, ou pièce non attendue).
 * Pièce attendue : celle que donne l'appelant (l'historique du dossier), sinon la liste unique
 * des opérations sans pièce (recherche-pieces.ts) ; un paiement par carte l'attend toujours.
 * « Prête » ne veut pas dire « à valider » : la validation reste à la règle de rapprochement.
 */
export function etatOperationDougs(
  op: Pick<OperationDougs, 'validee' | 'categorie' | 'justificatifs' | 'libelle' | 'nature'>,
  o: { pieceAttendue?: boolean } = {},
): EtatOperationDougs {
  if (op.validee) return 'validee'
  if (op.categorie === null) return 'a_qualifier'
  const attendue = o.pieceAttendue ?? pieceAttendue(op).attendue
  return attendue && op.justificatifs === 0 ? 'a_justifier' : 'prete'
}
